/**
 * The customer portal's API client.
 *
 * Not the Console's client (src/api/client.ts). The portal is served on the
 * licence host, signs in with a bearer token rather than a cookie session, and
 * has no CSRF token to carry. Every call is same-origin under /api/portal.
 *
 * The token lives in sessionStorage: it survives a reload but not closing the
 * tab, and it is dropped the moment the server answers 401 to a signed-in call.
 */

const BASE = "/api/portal";
const TOKEN_KEY = "mdp_portal_token";

export class PortalApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "PortalApiError";
    this.status = status;
  }
}

function readStored(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

let token: string | null = readStored();
const listeners = new Set<(token: string | null) => void>();

export function getToken(): string | null {
  return token;
}

export function setToken(next: string | null): void {
  if (next === token) return;
  token = next;
  try {
    if (next) sessionStorage.setItem(TOKEN_KEY, next);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage is blocked (private windows, strict policies): the session then
    // lasts as long as the page does.
  }
  listeners.forEach((listener) => listener(next));
}

export function onTokenChange(listener: (token: string | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

interface Options {
  body?: unknown;
  /** Send the bearer token (the default). Sign-in and the MFA challenge steps do not. */
  auth?: boolean;
  /**
   * End the session on a 401 (the default). Turned off where a 401 means
   * "wrong code" rather than "signed out" — activating MFA from Settings.
   */
  endSessionOn401?: boolean;
}

async function toError(response: Response): Promise<PortalApiError> {
  const text = await response.text().catch(() => "");
  let message = text.trim();
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === "object" && "detail" in parsed) {
      const detail = (parsed as { detail: unknown }).detail;
      message = typeof detail === "string" ? detail : JSON.stringify(detail);
    }
  } catch {
    // A malformed-body rejection is plain text, and the text is the message.
  }
  return new PortalApiError(response.status, message || `Request failed (${response.status})`);
}

async function send(method: string, path: string, options: Options = {}): Promise<Response> {
  const { body, auth = true, endSessionOn401 = true } = options;
  const headers: Record<string, string> = {};
  const sent = auth ? token : null;
  if (sent) headers.Authorization = `Bearer ${sent}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      cache: "no-store",
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new PortalApiError(
      0, "Could not reach the licence server. Check your connection and try again.",
    );
  }

  if (!response.ok) {
    const error = await toError(response);
    // Only drop the token this request carried: a late answer to an old
    // request must not sign out a session that has since been replaced.
    if (response.status === 401 && sent && endSessionOn401 && token === sent) setToken(null);
    throw error;
  }
  return response;
}

export async function request<T>(method: string, path: string, options?: Options): Promise<T> {
  const response = await send(method, path, options);
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown, options?: Omit<Options, "body">) =>
    request<T>("POST", path, { ...options, body }),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, { body }),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, { body }),
  del: <T>(path: string) => request<T>("DELETE", path),
};

function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Fall through to the plain form.
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/**
 * Download a file that needs the bearer header, which a plain link cannot
 * send: fetch it, then hand the browser a temporary object URL to save.
 * Returns the file name used. Releases run to hundreds of megabytes, so the
 * body is read in chunks and `onProgress` reports how far it has got.
 */
export async function download(
  path: string,
  fallbackName: string,
  onProgress?: (received: number, total: number | null) => void,
): Promise<string> {
  const response = await send("GET", path);
  const total = Number(response.headers.get("content-length")) || null;
  let blob: Blob;
  if (response.body && onProgress) {
    const reader = response.body.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      onProgress(received, total);
    }
    blob = new Blob(chunks);
  } else {
    blob = await response.blob();
  }
  const name = filenameFrom(response.headers.get("content-disposition")) || fallbackName;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.rel = "noopener";
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke later: some browsers read the blob after click() has returned.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return name;
}
